import React from 'react';
import { Group, Line, Rect, Text } from 'react-konva';
import WithCursorPointer from 'components/canvas/components/with-pointer-cursor';
import LinkShape from './components/link-shape';
import If from 'components/if-statement';
import { useCanvasStageScale } from 'state/canvas/stage';
import {
  useLinkMenuPositionOperation,
  useSelectedLinkOperations,
} from 'state/canvas/selected-link';
import useLinkConnectionOperations from 'components/canvas/hooks/use-link-connection-operations';
import useCanvas from 'components/canvas/hooks/use-canvas';
import useCurrentSurface from 'routes/surface/hooks/use-current-surface';
import { useNodeLinkingOperations } from 'state/canvas/node-linking';
import { useSelectedNodeOperations } from 'state/canvas/selected-node';
import { usePrevious, useSafeSetState } from 'hooks';
import forbiddenIcon from 'styles/icons/forbidden.svg';
import { isEmpty, throttle } from 'lodash';
import { color } from 'styles/settings';
import { isMobile } from 'react-device-detect';
import { LinkType, SurfaceNodePointsTo } from 'graphql/types';
import useFindNodeInCache from 'components/canvas/hooks/use-find-node';
import EdgeListeners from 'components/canvas/components/edge/components/edge-listeners';
import { Position } from 'types';
import { isDefined } from 'utils';

export interface EdgeProps {
  link: SurfaceNodePointsTo;
  isOneOfLinkNodesUpdated: boolean;
}

const colors = {
  white: color.neutral.white,
  green: color.brand.greenDark,
  gold: color.brand.gold,
};

const forbiddenImage = new window.Image();
forbiddenImage.src = forbiddenIcon;

function Edge({ link, isOneOfLinkNodesUpdated }: EdgeProps) {
  const { id: surfaceId } = useCurrentSurface() || {};

  const [isHidden, setIsLinkHidden] = React.useState(false);

  const [isSelected, setIsSelected] = React.useState(false);

  const [renderedAt, setRenderedAt] = useSafeSetState(new Date().toISOString());

  const { findNodeById } = useFindNodeInCache();

  const throttleSetRenderedAt = throttle(() => {
    setRenderedAt(new Date().toISOString());
  }, 50);

  useOnOneOfLinkNodesUpdated(isOneOfLinkNodesUpdated, throttleSetRenderedAt);

  useLinkNodesMovedListener(link, throttleSetRenderedAt);

  const {
    getIsLinkSelected,
    toggleLinkSelection,
    setSelectedLink,
  } = useSelectedLinkOperations();

  const { color: linkColor, endShape, startShape, linkType } =
    link.settings || {};

  const { getLinkConnectionPosition } = useLinkConnectionOperations(surfaceId);

  const { setSelectedNode, getSelectedNode } = useSelectedNodeOperations();

  const { showLinkArrow, getIsLinkArrowShown } = useNodeLinkingOperations();

  // @ts-ignore
  const { start, end } = getLinkConnectionPosition(link, 15);

  const color = linkColor ?? colors.green;

  const arrowMiddle = {
    x: (start?.x + end?.x) / 2,
    y: (start?.y + end?.y) / 2,
  };

  const points = [start?.x, start?.y, end?.x, end?.y];

  const onStageClick = React.useCallback(() => {
    if (isHidden) {
      setIsLinkHidden(false);
    }

    if (isSelected) {
      setIsSelected(false);
    }
  }, [isSelected, isHidden]);

  function onDragStartHandler() {
    const isSelected = getIsLinkSelected(link);
    const isEditable = link.settings?.isEditable;
    const linkArrowNotShown = getIsLinkArrowShown() === false;

    if (isSelected && isEditable && linkArrowNotShown) {
      if (getSelectedNode() !== link.from.id) {
        setSelectedNode(link.from.id);
      }
      setIsLinkHidden(true);
      showLinkArrow(link.from, undefined, link);
    }
  }

  function onClick() {
    if (isSelected) {
      toggleLinkSelection(link);
    } else {
      setSelectedLink(link);
    }
    setIsSelected((currentValue) => !currentValue);
  }

  function isLinkFromPlaceholderToContainingNode(link: SurfaceNodePointsTo) {
    const startNode = findNodeById(link?.from?.id);
    const endNode = findNodeById(link?.to?.id);

    if (endNode && startNode) {
      return (
        //@ts-ignore
        endNode?.contains?.id === startNode.id ||
        //@ts-ignore
        startNode?.contains?.id === endNode.id
      );
    }

    return false;
  }
  return React.useMemo(() => {
    if (isHidden) {
      return null;
    }

    if (isLinkFromPlaceholderToContainingNode(link)) {
      return null;
    }

    return (
      <>
        {isSelected && (
          <EdgeListeners link={link} onStageClick={onStageClick} />
        )}
        <WithCursorPointer>
          <Group id={link.from.id + link.to.id}>
            {/* Dummy hidden element used to fetch link middle position */}
            {isSelected && <DummyRect x={arrowMiddle.x} y={arrowMiddle.y} />}
            {startShape && (
              <LinkShape
                type={startShape}
                position={start}
                end={end}
                color={color}
              />
            )}
            <If condition={link.settings?.linkName}>
              <Text
                text={link.settings?.linkName as string}
                {...arrowMiddle}
                offsetY={-20}
              />
            </If>
            <Line
              hitStrokeWidth={isMobile ? 70 : 30}
              points={points}
              stroke={color}
              fill={color}
              strokeWidth={isSelected ? 10 : 8}
              pointerWidth={6}
              {...(linkType === LinkType.Dashed ? { dash: [15, 30] } : {})}
              {...(isSelected
                ? {
                    onTouchStart: onDragStartHandler,
                    onDragStart: onDragStartHandler,
                    draggable: true,
                  }
                : {})}
              {...{ [isMobile ? 'onTap' : 'onClick']: onClick }}
            />
            {endShape && (
              <LinkShape
                type={endShape}
                position={end}
                start={start}
                color={color}
              />
            )}
          </Group>
        </WithCursorPointer>
      </>
    );
  }, [isSelected, renderedAt, isHidden, link?.settings?.updatedAt]);
}

export default Edge;

function DummyRect(props: Position) {
  const ref = React.useRef<any>();

  const scale = useCanvasStageScale();

  const { setLinkMenuPosition } = useLinkMenuPositionOperation();

  React.useEffect(() => {
    showLinkMenu();
  }, [props.x, props.y, scale]);

  const showLinkMenu = throttle(() => {
    const linkMenuPosition = ref.current?.getAbsolutePosition();

    const offsetY = 100;

    if (linkMenuPosition) {
      const position = {
        x: linkMenuPosition.x,
        y: linkMenuPosition.y - offsetY * scale,
      };

      setLinkMenuPosition(position);
    }
  }, 150);

  return (
    <Rect
      ref={ref}
      width={400}
      height={50}
      x={props.x}
      y={props.y}
      scaleX={scale}
      scaleY={scale}
    />
  );
}

function useLinkNodesMovedListener(
  link: SurfaceNodePointsTo,
  callBack: () => void,
) {
  const { findShapeById } = useCanvas();
  const { findNodeFromLink } = useFindNodeInCache();

  React.useEffect(() => {
    // When stage ref is assigned trigger rerender to fetch correct values

    const fromShape = findShapeById(link.from.id);
    const toShape = findShapeById(link.to.id);

    fromShape?.on('xChange yChange', function () {
      callBack();
    });

    toShape?.on('xChange yChange', function () {
      callBack();
    });

    return () => {
      const fromNode = findNodeFromLink(link.from);
      const toNode = findNodeFromLink(link.to);

      if (isEmpty(fromNode?.pointsTo)) {
        fromShape?.off('xChange yChange');
      }

      if (isEmpty(toNode?.pointsTo)) {
        toShape?.off('xChange yChange');
      }
    };
  }, []);
}

function useOnOneOfLinkNodesUpdated(
  isOneOfLinkNodesUpdated: boolean,
  callback: Function,
) {
  const previousIsOneOfLinkNodesUpdated = usePrevious(isOneOfLinkNodesUpdated);

  const shouldUpdate =
    isDefined(previousIsOneOfLinkNodesUpdated) &&
    previousIsOneOfLinkNodesUpdated !== isOneOfLinkNodesUpdated &&
    isOneOfLinkNodesUpdated;

  React.useEffect(() => {
    shouldUpdate && callback();
  }, [shouldUpdate]);
}
